import { Client, ClientWithExtras } from './client';
import { Medication, ZipCode } from '../common';

import { BaseDataTransferObject } from '../base-data-transfer-object';
import { BillingInformation } from '../billing/billing-information';
import { Physician } from './physician';
import { ResponsibleParty } from './reponsible-party';
import { Room } from './room';
import { Unit } from './unit';

export class SexualOrientation extends BaseDataTransferObject {
  public code!: string;
  public name!: string;
}

export class GenderIdentity extends BaseDataTransferObject {
  public code!: string;
  public name!: string;
}

export class Patient extends BaseDataTransferObject {
  public code!: string;
  public unit!: Unit;
  public unitId!: number;
  public room!: Room;
  public roomId!: number;
  public firstName!: string;
  public lastName!: string;
  public middleName!: string;
  public gender!: string;
  public medicalRecordId!: string;
  public socialSecurityNumber!: string;
  public bed!: string;
  public phone!: string;
  public mobile!: string;
  public email!: string;
  public address!: string;
  public zipCodePlus4!: string;
  public sameAsClientAddress!: boolean;
  public birthDate!: string | number | Date;
  public zipCode!: ZipCode;
  public zipCodeName!: string;
  public client!: ClientWithExtras;
  public clientId!: number;
  public primaryPhysician!: Physician;
  public primaryPhysicianId!: number;
  public ethnicity!: number;
  public sexualOrientationId!: number;
  public sexualOrientation!: SexualOrientation;
  public genderIdentityId!: number;
  public genderIdentity!: GenderIdentity;
  public race!: string;
}

export class PatientEditable extends Patient {
  public dob!: Date;
  public medications!: Medication[];
  public diagnoses!: number[];
  public responsibleParties!: ResponsibleParty[];
  public insuranceInformation!: BillingInformation[];
}

export class PatientSearch extends PatientEditable {
  public code!: string;
}

export class PatientSearchWithoutCollections extends Patient {
  public code!: string;
}

export class TransientPatient extends PatientEditable {
  public recentDate!: Date;
  public shouldBeDeletedDate!: Date;
}

export class PatientClientPhysician {
  public id?: number;
  public clientId!: number;
  public physicianId?: number;
  public roomId?: number;
  public bed!: string;
}

export function arePatientEditablesEqual(a?: PatientEditable | null, b?: PatientEditable | null): boolean {
  return deepEqual(a, b);
}

function deepEqual(x: any, y: any): boolean {
  if (x === y) return true;

  if (x == null || y == null) return x === y;

  if (isDate(x) && isDate(y)) {
    return x.getTime() === y.getTime();
  }

  if (Array.isArray(x) && Array.isArray(y)) {
    return compareArrays(x, y);
  }

  if (isPlainObject(x) && isPlainObject(y)) {
    // Prefer id-based comparison for objects that carry an id
    const idKey = findIdKey(x) || findIdKey(y);
    if (idKey) {
      return x[idKey] === y[idKey];
    }

    const keys = new Set([...Object.keys(x), ...Object.keys(y)]);
    for (const key of keys) {
      if (!deepEqual(x[key], y[key])) return false;
    }
    return true;
  }

  // primitives (string | number | boolean | symbol) or mismatched types
  return x === y;
}

function compareArrays(a: any[], b: any[]): boolean {
  if (a.length !== b.length) return false;
  if (a.length === 0) return true;

  // If array of primitives -> order-insensitive compare by sorted stringified value
  if (a.every(isPrimitive) && b.every(isPrimitive)) {
    const sa = [...a].map(String).sort();
    const sb = [...b].map(String).sort();
    for (let i = 0; i < sa.length; i++) if (sa[i] !== sb[i]) return false;
    return true;
  }

  // If array of objects with ids -> compare sets of ids (order-insensitive)
  const idKeyA = findIdKeyInArray(a);
  const idKeyB = findIdKeyInArray(b);
  const idKey = idKeyA || idKeyB;
  if (idKey) {
    const idsA = new Set(a.map(item => item[idKey]));
    const idsB = new Set(b.map(item => item[idKey]));
    if (idsA.size !== idsB.size) return false;
    for (const id of idsA) if (!idsB.has(id)) return false;
    return true;
  }

  // Otherwise compare element-by-element (order-sensitive) using deepEqual
  for (let i = 0; i < a.length; i++) {
    if (!deepEqual(a[i], b[i])) return false;
  }
  return true;
}

function isPrimitive(v: any): boolean {
  return v === null || (typeof v !== 'object' && typeof v !== 'function');
}

function isDate(v: any): v is Date {
  return Object.prototype.toString.call(v) === '[object Date]';
}

function isPlainObject(v: any): boolean {
  return v !== null && typeof v === 'object' && !Array.isArray(v) && !isDate(v);
}

function findIdKey(obj: any): string | undefined {
  if (!isPlainObject(obj)) return undefined;
  if ('id' in obj) return 'id';
  if ('Id' in obj) return 'Id';
  // common domain-specific id keys
  const candidates = ['clientId', 'physicianId', 'unitId', 'roomId', 'primaryPhysicianId', 'sexualOrientationId', 'genderIdentityId'];
  for (const c of candidates) if (c in obj) return c;
  return undefined;
}

function findIdKeyInArray(arr: any[]): string | undefined {
  for (const item of arr) {
    const k = findIdKey(item);
    if (k) return k;
  }
  return undefined;
}
